<template>
<div>
    <Breadcrumbs main="" submain="Dashboard" title="Project Overview" />

    <div class="container-fluid">
        <div style="padding: 0px 12px; margin-bottom: 16px; display: flex; justify-content: end;">
            <b-button variant="primary" @click="goBack">
                <div style="display: flex; align-items: center; gap: 8px;">
                    <feather type="arrow-left" size="18px"></feather>
                    <h7>Back</h7>
                </div>
            </b-button>
        </div>
        <div class="row" v-if="!loading && !error">
            <!-- Project Name Header - Full Width -->
            <div class="col-12 mb-4">
                <div class="card project-header-card">
                    <div class="card-body">
                        <div style="margin: 8px 0px;">
                            <b-badge :variant="getStatusVariant(details.status)" pill class="status-badge">
                                {{ details.status }}
                            </b-badge>
                        </div>
                        <div class="d-flex justify-content-between align-items-start mb-3">
                            <div class="flex-grow-1">
                                <h4 class="mb-2 project-title">{{ details.title }}</h4>
                                <p class="text-muted mb-0 project-description">{{ details.details }}</p>
                            </div>

                        </div>

                        <div class="project-info-grid">
                            <div class="info-row">
                                <div class="info-label">
                                    <feather type="folder" size="16" class="mr-2"></feather>
                                    <span>Project Category:</span>
                                </div>
                                <div class="info-value">{{ details.category?.title || 'N/A' }}</div>
                            </div>

                            <div class="info-row">
                                <div class="info-label">
                                    <feather type="calendar" size="16" class="mr-2"></feather>
                                    <span>Start Date:</span>
                                </div>
                                <div class="info-value">{{ formatDate(details.start_date) }}</div>
                            </div>

                            <div class="info-row">
                                <div class="info-label">
                                    <feather type="clock" size="16" class="mr-2"></feather>
                                    <span>Deadline:</span>
                                </div>
                                <div class="info-value">{{ formatDate(details.deadline) }}</div>
                            </div>

                            <div class="info-row">
                                <div class="info-label">
                                    <feather type="user" size="16" class="mr-2"></feather>
                                    <span>Project Lead:</span>
                                </div>
                                <div class="info-value">
                                    <template v-for="(team, index) in details.teammates">
                                        <span v-if="team.project_lead" :key="index">
                                            <img class="img-40 rounded-circle" :class="{ 'border-blue': team.project_lead }" :src="host + team.user_id.additional_info?.photo || require('../../../assets/images/user/3.jpg')" :alt="team.user_name" v-b-tooltip.hover v-b-modal.modal-center-project-lead :title="team.user_name" />
                                            <!-- <b-avatar size="24" variant="primary" class="mr-2">
                                                {{ team.user_name.charAt(0) }}
                                            </b-avatar> -->
                                            {{ team.user_name }}
                                        </span>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Stats Cards Row -->
            <div class="col-xl-4 col-md-6 col-sm-12 mb-4">
                <div class="card stats-card h-100">
                    <div class="card-body d-flex flex-column">
                        <div class="d-flex justify-content-between align-items-center flex-grow-1">
                            <div>
                                <h6 class="text-muted mb-2">Total Tasks</h6>
                                <h3 class="mb-0 stats-number">{{ details.task }}</h3>
                            </div>
                            <div class="stats-icon bg-info">
                                <feather type="list"></feather>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Resolved Tasks -->
            <div class="col-xl-4 col-md-6 col-sm-12 mb-4">
                <div class="card stats-card h-100">
                    <div class="card-body d-flex flex-column">
                        <div class="d-flex justify-content-between align-items-center flex-grow-1">
                            <div>
                                <h6 class="text-muted mb-2">Resolved Tasks</h6>
                                <h3 class="mb-2 stats-number">{{ details.resolved }}</h3>
                                <small class="text-success">
                                    <feather type="trending-up" size="14"></feather>
                                    {{ calculatePercentage(details.resolved, details.task) }}% Complete
                                </small>
                            </div>
                            <div class="stats-icon bg-success">
                                <feather type="check-circle"></feather>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Remaining Tasks -->
            <div class="col-xl-4 col-md-6 col-sm-12 mb-4">
                <div class="card stats-card h-100">
                    <div class="card-body d-flex flex-column">
                        <div class="d-flex justify-content-between align-items-center flex-grow-1">
                            <div>
                                <h6 class="text-muted mb-2">Remaining Tasks</h6>
                                <h3 class="mb-2 stats-number">{{ details.remaining }}</h3>
                                <small class="text-warning">
                                    <feather type="clock" size="14"></feather>
                                    {{ calculatePercentage(details.remaining, details.task) }}% Pending
                                </small>
                            </div>
                            <div class="stats-icon bg-warning">
                                <feather type="alert-circle"></feather>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Total Budget -->

            <!-- Financial Summary Card -->
            <div class="col-xl-12 col-md-12 col-sm-12 mb-4">
                <div class="card stats-card">
                    <div class="card-header financial-header">
                        <div class="d-flex justify-content-between align-items-center">
                            <h5 class="mb-0">
                                <feather type="dollar-sign" size="20" class="mr-2"></feather>
                                Financial Summary
                            </h5>
                            <div class="stats-icon-small bg-primary">
                                <feather type="credit-card" size="20"></feather>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <!-- PO Value -->
                            <div class="col-md-3 mb-3">
                                <div class="financial-box">
                                    <div class="financial-icon bg-primary-light">
                                        <feather type="file-text" size="24" class="text-primary"></feather>
                                    </div>
                                    <div class="financial-content">
                                        <span class="financial-label">PO Value</span>
                                        <h4 class="financial-amount mb-0">৳{{ formatNumber(details.po_value) }}</h4>
                                        <small class="text-muted">Base Amount</small>
                                    </div>
                                </div>
                            </div>

                            <!-- VAT -->
                            <div class="col-md-3 mb-3">
                                <div class="financial-box">
                                    <div class="financial-icon bg-danger-light">
                                        <feather type="minus-circle" size="24" class="text-danger"></feather>
                                    </div>
                                    <div class="financial-content">
                                        <span class="financial-label">VAT ({{ details.po_vat }}%)</span>
                                        <h4 class="financial-amount mb-0 text-danger">৳{{ vatAmount }}</h4>
                                        <small class="text-muted">{{ details.val_type === 'inclusive' ? 'Inclusive' : 'Exclusive' }}</small>
                                    </div>
                                </div>
                            </div>

                            <!-- AIT -->
                            <div class="col-md-3 mb-3">
                                <div class="financial-box">
                                    <div class="financial-icon bg-danger-light">
                                        <feather type="minus-circle" size="24" class="text-danger"></feather>
                                    </div>
                                    <div class="financial-content">
                                        <span class="financial-label">AIT ({{ details.po_ait }}%)</span>
                                        <h4 class="financial-amount mb-0 text-danger">৳{{ aitAmount }}</h4>
                                        <small class="text-muted">{{ details.ait_type === 'inclusive' ? 'Inclusive' : 'Exclusive' }}</small>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3 mb-3">
                                <div class="financial-box">
                                    <div class="financial-icon bg-success">
                                        <feather type="trending-up" size="24" class="text-white"></feather>
                                    </div>
                                    <div class="financial-content">
                                        <span class="financial-label">Net Amount </span>
                                        <h4 class="financial-amount mb-0 text-success">৳{{ netAmount }}</h4>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <!-- Requisition Summary Card -->
            <div class="col-xl-12 col-md-12 col-sm-12 mb-4" v-b-modal.modal-prevent-requisition @click="RequisitionDetails()">
                <div class="card stats-card">
                    <div class="card-header financial-header">
                        <div class="d-flex justify-content-between align-items-center">
                            <h5 class="mb-0">
                                <feather type="shopping-cart" size="20" class="mr-2"></feather>
                                Requisition Summary
                            </h5>
                            <div class="stats-icon-small bg-primary">
                                <feather type="activity" size="20"></feather>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <!-- Total Budget -->
                            <div class="col-md-3 mb-3">
                                <div class="financial-box">
                                    <div class="financial-icon bg-primary-light">
                                        <feather type="dollar-sign" size="24" class="text-primary"></feather>
                                    </div>
                                    <div class="financial-content">
                                        <span class="financial-label">Total Budget</span>
                                        <h4 class="financial-amount mb-0">৳{{ formatNumber(details.budget) }}</h4>
                                        <small class="text-muted">Allocated Amount</small>
                                    </div>
                                </div>
                            </div>

                            <!-- Total Expense -->
                            <div class="col-md-3 mb-3">
                                <div class="financial-box">
                                    <div class="financial-icon bg-info-light">
                                        <feather type="trending-down" size="24" class="text-info"></feather>
                                    </div>
                                    <div class="financial-content">
                                        <span class="financial-label">Total Expense</span>
                                        <h4 class="financial-amount mb-0 text-info">৳{{ formatNumber(details.requisition.total) }}</h4>
                                        <small class="text-muted">Total Requested</small>
                                    </div>
                                </div>
                            </div>

                            <!-- Total Paid -->
                            <div class="col-md-3 mb-3">
                                <div class="financial-box">
                                    <div class="financial-icon bg-success-light">
                                        <feather type="check-circle" size="24" class="text-success"></feather>
                                    </div>
                                    <div class="financial-content">
                                        <span class="financial-label">Total Paid</span>
                                        <h4 class="financial-amount mb-0 text-success">৳{{ formatNumber(details.requisition.paid) }}</h4>
                                        <small class="text-muted">Completed Payments</small>
                                    </div>
                                </div>
                            </div>

                            <!-- Total Due -->
                            <div class="col-md-3 mb-3">
                                <div class="financial-box">
                                    <div class="financial-icon bg-danger-light">
                                        <feather type="clock" size="24" class="text-danger"></feather>
                                    </div>
                                    <div class="financial-content">
                                        <span class="financial-label">Total Due</span>
                                        <h4 class="financial-amount mb-0 text-danger">৳{{ formatNumber(details.requisition.due) }}</h4>
                                        <small class="text-muted">Pending Payments</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Invoice Summary Card -->
            <div class="col-xl-12 col-md-12 col-sm-12 mb-4" v-b-modal.modal-prevent-invoice @click="InvoiceDetails()">
                <div class="card stats-card">
                    <div class="card-header financial-header">
                        <div class="d-flex justify-content-between align-items-center">
                            <h5 class="mb-0">
                                <feather type="file-text" size="20" class="mr-2"></feather>
                                Invoice Summary
                            </h5>
                            <div class="stats-icon-small bg-primary">
                                <feather type="clipboard" size="20"></feather>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <!-- Total Invoice -->
                            <div class="col-md-4 mb-3">
                                <div class="financial-box">
                                    <div class="financial-icon bg-primary-light">
                                        <feather type="file-text" size="24" class="text-primary"></feather>
                                    </div>
                                    <div class="financial-content">
                                        <span class="financial-label">Total Invoice</span>
                                        <h4 class="financial-amount mb-0">৳{{ formatNumber(details.invoice.total) }}</h4>
                                        <small class="text-muted">Total Invoiced Amount</small>
                                    </div>
                                </div>
                            </div>

                            <!-- Total Paid -->
                            <div class="col-md-4 mb-3">
                                <div class="financial-box">
                                    <div class="financial-icon bg-success-light">
                                        <feather type="check-circle" size="24" class="text-success"></feather>
                                    </div>
                                    <div class="financial-content">
                                        <span class="financial-label">Total Paid</span>
                                        <h4 class="financial-amount mb-0 text-success">৳{{ formatNumber(details.invoice.paid) }}</h4>
                                        <small class="text-muted">Completed Payments</small>
                                    </div>
                                </div>
                            </div>

                            <!-- Total Due -->
                            <div class="col-md-4 mb-3">
                                <div class="financial-box">
                                    <div class="financial-icon bg-danger-light">
                                        <feather type="clock" size="24" class="text-danger"></feather>
                                    </div>
                                    <div class="financial-content">
                                        <span class="financial-label">Total Due</span>
                                        <h4 class="financial-amount mb-0 text-danger">৳{{ formatNumber(details.invoice.due) }}</h4>
                                        <small class="text-muted">Pending Payments</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- <div class="col-xl-3 col-md-6 col-sm-12 mb-4">
                <div class="card stats-card h-100">
                    <div class="card-body d-flex flex-column">
                        <div class="d-flex justify-content-between align-items-center flex-grow-1">
                            <div>
                                <h6 class="text-muted mb-2">Total Budget</h6>
                                <h3 class="mb-0 stats-number">৳{{ formatNumber(details.budget) }}</h3>
                            </div>
                            <div class="stats-icon bg-primary">
                                <feather type="dollar-sign"></feather>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-xl-3 col-md-6 col-sm-12 mb-4">
                <div class="card stats-card h-100">
                    <div class="card-body d-flex flex-column">
                        <div class="d-flex justify-content-between align-items-center flex-grow-1">
                            <div>
                                <h6 class="text-muted mb-2">Total Expense</h6>
                                <h3 class="mb-0 stats-number">৳{{ formatNumber(details.requisition.total) }}</h3>
                            </div>
                            <div class="stats-icon bg-info">
                                <feather type="trending-down"></feather>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-xl-3 col-md-6 col-sm-12 mb-4">
                <div class="card stats-card h-100">
                    <div class="card-body d-flex flex-column">
                        <div class="d-flex justify-content-between align-items-center flex-grow-1">
                            <div>
                                <h6 class="text-muted mb-2">Total Paid</h6>
                                <h3 class="mb-0 stats-number">৳{{ formatNumber(details.requisition.paid) }}</h3>
                            </div>
                            <div class="stats-icon bg-warning">
                                <feather type="check-circle"></feather>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-xl-3 col-md-6 col-sm-12 mb-4">
                <div class="card stats-card h-100">
                    <div class="card-body d-flex flex-column">
                        <div class="d-flex justify-content-between align-items-center flex-grow-1">
                            <div>
                                <h6 class="text-muted mb-2">Total Due</h6>
                                <h3 class="mb-0 stats-number">৳{{ formatNumber(details.requisition.due) }}</h3>
                            </div>
                            <div class="stats-icon bg-success">
                                <feather type="clock"></feather>
                            </div>
                        </div>
                    </div>
                </div>
            </div> -->

        </div>

        <!-- Loading State -->
        <div class="row" v-else="loading || error">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <div class="loader-box">
                            <div class="loader-10"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error State -->
        <!-- <div class="row" v-else-if="error">
            <div class="col-12">
                <div class="card">
                    <div class="card-body text-center py-5">
                        <feather type="alert-circle" size="48" class="text-danger mb-3"></feather>
                        <h5>Error Loading Dashboard</h5>
                        <b-button variant="primary" @click="fetchDashboardData" class="mt-3">
                            <feather type="refresh-cw" size="16" class="mr-2"></feather>
                            Retry
                        </b-button>
                    </div>
                </div>
            </div>
        </div> -->

    </div>


       <!-- Invoice Modal -->
        <b-modal id="modal-prevent-invoice" size="xl" ref="modal" title="Invoices" hide-footer>
            <!-- <button class="btn btn-primary mb-4" type="submit" @click="toInvoiceRequest">
                <h7>Create a Invoice</h7>
            </button> -->

            <div>
                <b-table show-empty :items="invoiceList" :fields="tableInvoice" head-variant="light">
                    <template #cell(index)="data">
                        {{ data.index + 1 }}
                    </template>
                    <template #cell(invoice_id)="data">
                        <span>
                            <router-link :to="`/finance-and-accounts/finance/invoice/${data.item.invoice_id}`" class="link-style">
                                {{ data.item.invoice_id }}
                            </router-link>
                        </span>
                    </template>
                    <template #cell(status)="data">
                        <span v-if="data.item.status == 'FULLY-PAID'" class="badge" :class="'badge-primary'">{{ data.item.status }}</span>
                        <span v-if="data.item.status == 'DUE'" class="badge" :class="'badge-warning'">{{ data.item.status }}</span>
                        <span v-if="data.item.status == 'PARTIALLY-PAID'" class="badge" :class="'badge-info'">{{ data.item.status }}</span>
                    </template>
                    <template #cell(subtotal)="data">
                        <div class="d-flex flex-column" style="width: 200px;">
                            <span class="font-weight-bold">Total: {{ data.item.subtotal }} {{ data.item.currency }}</span>
                            <span> <span class="text-success font-weight-bold">Paid:</span> {{ data.item.paid_amount }} {{ data.item.currency }}</span>
                            <span> <span class="text-danger font-weight-bold">Due:</span> {{ data.item.due_amount }} {{ data.item.currency }}</span>
                        </div>
                    </template>
                </b-table>
            </div>
        </b-modal>

        <!-- Requisition Modal -->
        <b-modal id="modal-prevent-requisition" size="xl" ref="modal" title="Requisitions" hide-footer>
            <!-- <button class="btn btn-primary mb-4" type="submit" @click="toRequisitionRequest">
                <h7>Create a requisition</h7>
            </button> -->

            <div>
                <b-table show-empty :items="requisitionList" :fields="tableRequisition" head-variant="light">
                    <template #cell(index)="data">
                        {{ data.index + 1 }}
                    </template>
                    <template #cell(requisition_id)="data">
                        <span>
                            <router-link :to="`/finance-and-accounts/account/requisition/view/${data.item.id}`" class="link-style">
                                {{ data.item.requisition_id }}
                            </router-link>
                        </span>
                    </template>
                    <template #cell(raised_by)="data">
                        <span>{{ data.item.raised_by?.username }}</span>
                    </template>
                    <template #cell(status)="data">
                        <span v-if="data.item.status == 'PENDING'" class="badge" :class="'badge-info'">{{ data.item.status }}</span>
                        <span v-if="data.item.status == 'DUE'" class="badge" :class="'badge-warning'">{{ data.item.status }}</span>
                        <span v-if="data.item.status == 'APPROVED'" class="badge" :class="'badge-success'">{{ data.item.status }}</span>
                        <span v-if="data.item.status == 'PARTIALLY-PAID'" class="badge" :class="'badge-info'">{{ data.item.status }}</span>
                        <span v-if="data.item.status == 'REJECTED'" class="badge" :class="'badge-danger'">{{ data.item.status }}</span>
                    </template>
                    <template #cell(grand_total)="data">
                        <div class="d-flex flex-column" style="width: 150px;">
                            <span class="font-weight-bold">Total: {{ data.item.grand_total }}</span>
                            <span><span class="text-success font-weight-bold">Paid:</span> {{ data.item.paid_amount }}</span>
                            <span><span class="text-danger font-weight-bold">Due:</span> {{ data.item.due_amount }}</span>
                        </div>
                    </template>
                    <template #cell(approved_by)="data">
                        <span>
                            <div v-if="data.item.approved_by !== null">
                                {{ data.item.approved_by?.full_name }}
                            </div>
                            <div v-else>
                                <p>N/A</p>
                            </div>
                        </span>
                    </template>
                </b-table>
            </div>
        </b-modal>
</div>
</template>

<script>
import {
    mapState
} from 'vuex';
import moment from "moment";
import {
    myHost
} from "../../../_helpers/host";
import {
    computed
} from 'vue';
export default {
    components: {

    },

    data() {
        return {
            host: myHost.host,
            tableInvoice: [
                {
                    key: 'index',
                    label: '#'
                },
                {
                    key: 'invoice_id',
                    label: 'Invoice ID',
                    sortable: true
                },
                {
                    key: 'invoice_date',
                    label: 'Invoice Date',
                    sortable: true
                },
                {
                    key: 'due_date',
                    label: 'Due Date',
                    sortable: true
                },
                {
                    key: 'status',
                    label: 'Status',
                    sortable: true
                },
                {
                    key: 'subtotal',
                    label: 'Amount',
                    sortable: true
                }
            ],
            tableRequisition: [
                {
                    key: 'index',
                    label: '#'
                },
                {
                    key: 'requisition_id',
                    label: 'Requisition ID',
                    sortable: true
                },
                {
                    key: 'raised_by',
                    label: 'Raised By',
                    sortable: true
                },
                {
                    key: 'status',
                    label: 'Status',
                    sortable: true
                },
                {
                    key: 'approved_by',
                    label: 'Approved By',
                    sortable: true
                },
                {
                    key: 'grand_total',
                    label: 'Amount',
                    sortable: true
                }
            ],
        };
    },

    mounted() {

    },

    created() {
        this.$store.dispatch("project/fetchProjectTrackingDetails", this.$route.params.id).then(result => {

        })

    },

    computed: {
        ...mapState({
            details: state => state.project.project_tracking_detail,
            loading: state => state.project.trackingDetailLoading,
            error: state => state.project.trackingDetailError,

            requisitionList: state => state.project.requisition_by_project,
            invoiceList: state => state.project.invoice_by_project,

        }),

        // Remove the "computed()" wrapper - just define them directly
        basePrice() {
            if (!this.details ?.po_value || !this.details ?.val_type || !this.details ?.ait_type) return 0;

            const total = Number(this.details.po_value);
            const po_vat = Number(this.details.po_vat || 0);
            const po_ait = Number(this.details.po_ait || 0);

            if (this.details.val_type === 'inclusive' && this.details.ait_type === 'inclusive') {
                const divisor = 1 + (po_vat + po_ait) / 100;
                const base = total / divisor;
                return Math.round((base + Number.EPSILON) * 100) / 100;
            }

            let divisor = 1;
            if (this.details.val_type === 'inclusive') {
                divisor += po_vat / 100;
            }
            if (this.details.ait_type === 'inclusive') {
                divisor += po_ait / 100;
            }

            const base = total / divisor;
            return Math.round((base + Number.EPSILON) * 100) / 100;
        },

        vatAmount() {
            if (this.details ?.po_vat && this.details ?.val_type && this.details ?.po_value) {
                const po_vat = Number(this.details.po_vat);
                return Math.round((this.basePrice * po_vat / 100 + Number.EPSILON) * 100) / 100;
            }
            return 0;
        },

        aitAmount() {
            if (this.details ?.po_ait && this.details ?.ait_type && this.details ?.po_value) {
                const po_ait = Number(this.details.po_ait);
                return Math.round((this.basePrice * po_ait / 100 + Number.EPSILON) * 100) / 100;
            }
            return 0;
        },
        netAmount() {
            if (!this.details?.po_value) return 0;

            const poValue = Number(this.details.po_value);

            const vatRate =
                this.details?.val_type === 'inclusive'
                    ? Number(this.details.po_vat || 0)
                    : 0;

            const aitRate =
                this.details?.ait_type === 'inclusive'
                    ? Number(this.details.po_ait || 0)
                    : 0;

       
            const totalRate = vatRate + aitRate;

            if (totalRate === 0) {
                return Math.round((poValue + Number.EPSILON) * 100) / 100;
            }

            const net = poValue / (1 + totalRate / 100);

            return Math.round((net + Number.EPSILON) * 100) / 100;
        },


        subtotal() {
            const total = Number(this.details ?.po_value || 0);
            const discount = Number(this.details ?.discount || 0);

            // Case 1: both VAT and AIT are inclusive → just apply discount to total
            if (this.details ?.val_type === 'inclusive' && this.details ?.ait_type === 'inclusive') {
                const discounted = total - (total * discount / 100);
                return Math.round((discounted + Number.EPSILON) * 100) / 100;
            }

            // Case 2: Exclusive taxes → add them first
            let calculated = total;

            if (this.details ?.val_type === 'exclusive') {
                calculated += this.vatAmount;
            }

            if (this.details ?.ait_type === 'exclusive') {
                calculated += this.aitAmount;
            }

            // Apply discount to final calculated total
            const discounted = calculated - (calculated * discount / 100);
            return Math.round((discounted + Number.EPSILON) * 100) / 100;
        }

    },

    watch: {

    },

    methods: {
        goBack() {
            if (window.history.length > 1) {
                this.$router.back();
            } else {
                this.$router.push('/dashboard');
            }
        },
        InvoiceDetails() {
            this.$store.dispatch("project/fetchInvoiceByProject", this.$route.params.id)
        },

        RequisitionDetails() {
            this.$store.dispatch("project/fetchRequisitionByProject", this.$route.params.id)
        },
        calculatePercentage(value, total) {
            if (total === 0) return 0;
            return Math.round((value / total) * 100);
        },
        formatNumber(num) {
            return new Intl.NumberFormat('en-US').format(num);
        },
        formatDate(date) {
            return moment(date).format('MMM DD, YYYY');
        },
        getStatusVariant(status) {
            const variants = {
                'APPROVED': 'primary',
                'PENDING': 'danger',
                'WORKING': 'info',
                'COMPLETED': 'success',
                'PAUSED': 'danger',
                'POSTPONED': 'warning',

            };
            return variants[status] || 'secondary';
        },
        calculateVAT() {
            return (this.details.po_value * this.details.po_vat) / 100;
        },

        calculateAIT() {
            return (this.details.po_value * this.details.po_ait) / 100;
        },
        calculateTotal() {
            return this.details.po_value + this.calculateVAT() - this.calculateAIT();
        },
        fetchDashboardData() {
            this.loading = true;
            this.error = null;

            this.$store.dispatch('project/fetchProjectTrackingDetails', this.$route.params.id)
                .then(response => {
                    this.details = response.data;
                    this.loading = false;
                })
                .catch(error => {
                    this.error = error.message;
                    this.loading = false;
                });

            setTimeout(() => {
                this.loading = false;
            }, 1000);
        }
    },
};
</script>

<style scoped>
.bg-info-light {
    background-color: #e7f3ff;
    color: #0dcaf0;
}

.bg-warning-light {
    background-color: #fff3cd; /* Bootstrap warning light */
    color: #ffc107;
}
.img-40 {
    width: 40px !important;
    height: 40px !important;
}

/* Project Header Card */
.project-header-card {
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-radius: 10px;
}

.project-title {
    color: #2c3e50;
    font-weight: 600;
    word-break: break-word;
}

.project-description {
    font-size: 0.95rem;
    line-height: 1.6;
}

.status-badge {
    font-size: 0.875rem;
    padding: 8px 16px !important;
    font-weight: 500;
    letter-spacing: 0.3px;
}

/* Project Info Grid */
.project-info-grid {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
}

.info-row {
    display: flex;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #e9ecef;
}

.info-row:last-child {
    border-bottom: none;
}

.info-label {
    display: flex;
    align-items: center;
    min-width: 180px;
    font-weight: 500;
    color: #6c757d;
    font-size: 0.9rem;
}

.info-value {
    display: flex;
    align-items: center;
    color: #2c3e50;
    font-size: 0.95rem;
    font-weight: 500;
}

/* Stats Cards */
.stats-card {
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    transition: all 0.3s ease;
}

.stats-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.stats-card .card-body {
    padding: 1.5rem;
}

.stats-number {
    font-weight: 700;
    color: #2c3e50;
}

.stats-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
}

.stats-icon-small {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}

/* Financial Summary */
.financial-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 10px 10px 0 0 !important;
    padding: 1rem 1.5rem;
}

.financial-header h5 {
    color: white;
    display: flex;
    align-items: center;
}

.financial-box {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 10px;
    transition: all 0.3s ease;
    height: 100%;
}

.financial-box:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.financial-icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.bg-primary-light {
    background-color: rgba(102, 126, 234, 0.1);
}

.bg-success-light {
    background-color: rgba(40, 167, 69, 0.1);
}

.bg-danger-light {
    background-color: rgba(220, 53, 69, 0.1);
}

.financial-content {
    flex-grow: 1;
}

.financial-label {
    display: block;
    color: #6c757d;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 8px;
}

.financial-amount {
    font-weight: 700;
    color: #2c3e50;
}

/* Total Box */
.total-box {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 24px;
    border-radius: 10px;
    color: white;
}

.total-box h5,
.total-box h2 {
    color: white;
}

.total-box .text-muted {
    color: rgba(255, 255, 255, 0.8) !important;
}

@media (max-width: 768px) {
    .info-label {
        min-width: 140px;
        font-size: 0.85rem;
    }

    .info-value {
        font-size: 0.9rem;
    }

    .stats-icon {
        width: 48px;
        height: 48px;
    }

    .financial-box {
        padding: 15px;
    }

    .financial-icon {
        width: 50px;
        height: 50px;
    }
}

/* Card Utilities */
.card {
    border: none;
}

.h-100 {
    height: 100% !important;
}
</style>
